/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable promise/always-return */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {
  type ReadonlySignal,
  type Signal,
  useComputed,
  useSignal,
} from '@preact/signals-react'
// eslint-disable-next-line @typescript-eslint/naming-convention
import moment from 'moment'
import react from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { createNotification } from '@/components/notification'
import { PUBLIC_ROUTE } from '@/constants'
import type {
  TForgotPwdData,
  TResetPwdData,
  TSignIn,
  TSignupData,
} from '@/interfaces'
import { AuthState } from '@/state'
import { authStateActions, paramsStateActions } from '@/state/actions'

interface AuthResult {
  handleChangeCurrentRole: (roleId: number) => void
  handleForgotPwd: (values: TForgotPwdData) => void
  handleLogin: (values: TSignIn) => void
  handleLogout: () => void
  handleReloadRole: () => void
  handleResetPwd: (values: TResetPwdData) => void
  handleSignup: (values: TSignupData) => void
  isLoading: Signal<boolean>
}

export const useAuth = (): AuthResult => {
  const isLoading = useSignal(false)
  const navigate = useNavigate()

  const handleChangeCurrentRole = (roleId: number): void => {
    isLoading.value = true
    authStateActions
      .handleChangeCurrentRole(roleId)
      .then(() =>
        createNotification(
          'success',
          'Rol actualizado',
          'Ahora puedes seguir utilizando la plataforma con el rol seleccionado'
        )
      )
      .finally(() => {
        isLoading.value = false
      })
      .catch((error) => {
        createNotification('error', 'Error', error.message as string)
        console.error('CHANGECURRENTROLE::ERROR', error)
      })
  }

  const handleReloadRole = (): void => {
    isLoading.value = true
    authStateActions
      .reloadCurrentRole()
      .then(() =>
        createNotification(
          'success',
          'Rol refrescado',
          'Ahora puedes seguir utilizando la plataforma con el mismo rol'
        )
      )
      .finally(() => {
        isLoading.value = false
      })
      .catch((error) => {
        createNotification('error', 'Error', error.message as string)
        console.error('RELOADCURRENTROLE::ERROR', error)
      })
  }

  const handleLogin = (values: TSignIn): void => {
    isLoading.value = true

    authStateActions
      .handleLogin(values)
      .finally(() => {
        isLoading.value = false
      })
      .catch((error) => {
        createNotification(
          'error',
          'Error',
          (error.message as string) ||
            'Usuario y/o contraseña incorrecto, verifica tus datos y vuelve a intentarlo'
        )
        console.error('SIGNIN::ERROR', error)
      })
  }

  const handleLogout = (): void => {
    authStateActions.handleLogout().catch((error) => {
      createNotification('error', 'Error', error.message as string)
      console.error('LOGOUT::ERROR', error)
    })
  }

  const handleSignup = (signupData: TSignupData): void => {
    isLoading.value = true
    signupData.birthdate = moment(signupData.birthdate).format('YYYY-MM-DD')
    authStateActions
      .handleSignup(signupData)
      .then(() =>
        createNotification(
          'success',
          'Registro exitoso',
          'Te invitamos a revisar tu correo para realizar la activación de tu cuenta'
        )
      )
      .finally(() => {
        isLoading.value = false
        navigate(PUBLIC_ROUTE.SIGN_IN)
      })
      .catch((error) => {
        createNotification('error', 'Error', error.message as string)
        console.error('SIGNUP::ERROR', error)
      })
  }

  const handleForgotPwd = (forgotPwdData: TForgotPwdData): void => {
    isLoading.value = true
    authStateActions
      .handleForgotPwd(forgotPwdData)
      .finally(() => {
        isLoading.value = false
      })
      .then(() =>
        createNotification(
          'success',
          'Proceso iniciado',
          'Te invitamos a revisar tu correo para continuar con el proceso de reestablecimiento de contraseña'
        )
      )
      .catch((error) => {
        createNotification('error', 'Error', error.message as string)
        console.error('FORGOTPWD::ERROR', error)
      })
  }

  const handleResetPwd = (resetPwdData: TResetPwdData): void => {
    isLoading.value = true
    authStateActions
      .handleResetPwd(resetPwdData)
      .finally(() => {
        isLoading.value = false
      })
      .then(() =>
        createNotification(
          'success',
          'Actualización exitosa',
          'El reestablecimiento de la contraseña se ha realizado exitosamente'
        )
      )
      .catch((error) => {
        createNotification('error', 'Error', error.message as string)
        console.error('RESETPWD::ERROR', error)
      })
  }

  return {
    handleChangeCurrentRole,
    handleForgotPwd,
    handleLogin,
    handleLogout,
    handleReloadRole,
    handleResetPwd,
    handleSignup,
    isLoading,
  }
}

interface ResetPwdResult {
  isValidated: Signal<boolean>
}

export const useResetPwd = (): ResetPwdResult => {
  const isValidated = useSignal(false)
  const navigate = useNavigate()
  const accessTokenSignal = useSignal<string | null>(null)

  const [searchParams] = useSearchParams()

  const accessToken = react.useMemo(
    () => searchParams.get('accessToken'),
    [searchParams]
  )

  const handleCheckResetToken = react.useCallback((): void => {
    if (accessToken && accessToken !== accessTokenSignal.peek()) {
      authStateActions
        .handleCheckResetPwdToken(accessToken)
        .then(() => {
          isValidated.value = true
        })
        .catch((error) => {
          createNotification('error', 'Error', error.message as string)
          console.error('CHECKRESETPWDTOKEN::ERROR', error)
          navigate('/signin')
        })
    }
  }, [])

  react.useEffect(() => {
    handleCheckResetToken()
  }, [accessToken, handleCheckResetToken])

  return {
    isValidated,
  }
}

export const useActivateAccount = (): void => {
  const navigate = useNavigate()
  const accessTokenSignal = useSignal<string | null>(null)

  const [searchParams] = useSearchParams()

  const accessToken = react.useMemo(
    () => searchParams.get('accessToken'),
    [searchParams]
  )

  const handleActivateAccountToken = (): void => {
    if (accessToken && accessToken !== accessTokenSignal.peek()) {
      accessTokenSignal.value = accessToken
      authStateActions
        .handleActivateAccount(accessToken)
        .then(() =>
          createNotification(
            'success',
            'Proceso exitoso',
            'La cuenta ha sido activada exitosamente'
          )
        )
        .catch((error) => {
          createNotification('error', 'Error', error.message as string)
          console.error('ACTIVATEACCOUNT::ERROR', error)
          navigate('/signin')
        })
    }
  }

  react.useEffect(() => {
    handleActivateAccountToken()
  }, [accessToken])
}

interface AuthSignup {
  handleSignup: (values: TSignupData) => void
  isLoading: Signal<boolean>
  isLoadingIdTypes: Signal<boolean>
}
export const useAuthSignUp = (): AuthSignup => {
  const { handleSignup, isLoading } = useAuth()
  const isLoadingIdTypes = useSignal(true)
  const navigate = useNavigate()

  const handleGetIdTypes = (): void => {
    paramsStateActions
      .getIdTypes()
      .finally(() => {
        isLoadingIdTypes.value = false
      })
      .catch((error) => {
        createNotification('error', 'Error', error.message as string)
        console.error('PARAMSIDTYPES::ERROR', error)
        navigate('/signin')
      })
  }

  react.useEffect(() => {
    handleGetIdTypes()
  }, [])

  return { handleSignup, isLoading, isLoadingIdTypes }
}

export const useCheckPathAccess = (): void => {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const handleCheckPathAccess = (): void => {
    if (!AuthState.routes.value.includes(pathname)) {
      navigate('/')
    }
  }

  react.useEffect(() => {
    handleCheckPathAccess()
  }, [pathname, AuthState.routes.value])
}

interface ICheckAccessResult {
  hasAccess: ReadonlySignal<boolean>
}
export const useCheckAccess = (
  permission: string | string[]
): ICheckAccessResult => {
  const hasAccess = useComputed(() => {
    const currentUserPermissions = AuthState.permissions.value
    if (Array.isArray(permission)) {
      return !permission.some((item) => !currentUserPermissions.includes(item))
    }

    return currentUserPermissions.includes(permission) ?? false
  })

  return {
    hasAccess,
  }
}
